/*
  Zephyr — runtime behaviours layered on Dawn.

  All client-side state lives in localStorage with the 'zephyr-' prefix.
  No external dependencies; vanilla DOM + fetch.

  Modules:
    - wishlistInit         heart toggle; persists product IDs locally
    - recentlyViewedInit   logs current product handle on PDP load
    - recentRenderInit     hydrates [data-zephyr-recent-list] via Shopify's
                           Section Rendering API (zephyr-product-card)
    - tabsInit             generic [data-zephyr-tabs] tab UI
    - gridViewToggleInit   collection-page 4↔3 density toggle
    - viewMoreInit         collection-page "View More" AJAX pagination —
                           injects the button by reading Dawn's existing
                           pagination, then hides Dawn's pagination
*/

(function () {
  'use strict';

  const LS_WISHLIST = 'zephyr-wishlist';
  const LS_RECENT = 'zephyr-recently-viewed';
  const LS_GRID_DENSITY = 'zephyr-grid-density';
  const RECENT_MAX = 12;

  function lsRead(key) {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); }
    catch (_) { return []; }
  }
  function lsWrite(key, arr) {
    try { localStorage.setItem(key, JSON.stringify(arr)); } catch (_) {}
  }

  // ------- WISHLIST -------
  // Stored as an array of objects {id, handle, title, url, image, price, variantId}.
  // Legacy bare-string IDs are normalised to {id} so old saves don't break.
  function wishlistList() {
    return lsRead(LS_WISHLIST).map(function (it) {
      return (typeof it === 'string') ? { id: it } : it;
    }).filter(function (it) { return it && it.id != null; });
  }
  function wishlistHas(list, id) {
    return list.some(function (it) { return String(it.id) === String(id); });
  }

  function wishlistInit() {
    document.querySelectorAll('[data-zephyr-wishlist]').forEach(function (btn) {
      if (btn._zwBound) return;
      btn._zwBound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var id = btn.dataset.productId;
        var current = wishlistList();
        var idx = current.findIndex(function (it) { return String(it.id) === String(id); });
        if (idx > -1) {
          current.splice(idx, 1);
        } else {
          current.push({
            id: id,
            handle: btn.dataset.productHandle || '',
            title: btn.dataset.productTitle || '',
            url: btn.dataset.productUrl || (btn.dataset.productHandle ? '/products/' + btn.dataset.productHandle : '#'),
            image: btn.dataset.productImage || '',
            price: btn.dataset.productPrice || '',
            variantId: btn.dataset.variantId || ''
          });
        }
        lsWrite(LS_WISHLIST, current);
        updateWishlistUI();
      });
    });
    updateWishlistUI();
  }

  function setWishlistState(btn, on) {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.classList.toggle('is-saved', on);
  }

  function updateWishlistUI() {
    var list = wishlistList();
    document.querySelectorAll('[data-zephyr-wishlist-count]').forEach(function (b) {
      if (list.length > 0) { b.textContent = list.length > 99 ? '99+' : String(list.length); b.hidden = false; }
      else { b.textContent = ''; b.hidden = true; }
    });
    document.querySelectorAll('[data-zephyr-wishlist]').forEach(function (btn) {
      setWishlistState(btn, wishlistHas(list, btn.dataset.productId));
    });
    var drawer = document.querySelector('[data-zephyr-wishlist-drawer]');
    if (drawer && drawer.classList.contains('is-open')) renderWishlistDrawer();
  }

  function zwEscape(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function renderWishlistDrawer() {
    var listEl = document.querySelector('[data-zephyr-wishlist-list]');
    var emptyEl = document.querySelector('[data-zephyr-wishlist-empty]');
    var countText = document.querySelector('[data-zephyr-wishlist-count-text]');
    if (!listEl) return;
    var items = wishlistList();
    if (countText) countText.textContent = items.length ? '(' + items.length + ')' : '';
    if (!items.length) {
      listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    listEl.innerHTML = items.map(function (it) {
      var url = it.url || (it.handle ? '/products/' + it.handle : '#');
      var img = it.image ? '<img src="' + it.image + '" alt="' + zwEscape(it.title) + '" loading="lazy" width="70" height="90">' : '';
      var add = it.variantId ? '<button type="button" class="zephyr-wl-item__add" data-zephyr-wishlist-add="' + it.variantId + '">Add to cart</button>' : '';
      return '<div class="zephyr-wl-item" data-id="' + it.id + '">' +
        '<a class="zephyr-wl-item__media" href="' + url + '">' + img + '</a>' +
        '<div class="zephyr-wl-item__info">' +
          '<a class="zephyr-wl-item__title" href="' + url + '">' + zwEscape(it.title) + '</a>' +
          (it.price ? '<div class="zephyr-wl-item__price">' + zwEscape(it.price) + '</div>' : '') +
          '<div class="zephyr-wl-item__actions">' + add +
            '<button type="button" class="zephyr-wl-item__remove" data-zephyr-wishlist-remove="' + it.id + '">Remove</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  function setWishlistDrawer(open) {
    var drawer = document.querySelector('[data-zephyr-wishlist-drawer]');
    if (!drawer) return;
    if (open) {
      renderWishlistDrawer();
      drawer.hidden = false;
      void drawer.offsetHeight;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('zephyr-wl-open');
    } else {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('zephyr-wl-open');
      setTimeout(function () { if (!drawer.classList.contains('is-open')) drawer.hidden = true; }, 320);
    }
  }

  function zwUpdateCartCount(count) {
    var bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
    if (bubble) bubble.textContent = count;
  }

  function wishlistDrawerInit() {
    document.querySelectorAll('[data-zephyr-wishlist-open]').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); setWishlistDrawer(true); });
    });
    var drawer = document.querySelector('[data-zephyr-wishlist-drawer]');
    if (!drawer) return;
    drawer.querySelectorAll('[data-zephyr-wishlist-close]').forEach(function (el) {
      el.addEventListener('click', function () { setWishlistDrawer(false); });
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') setWishlistDrawer(false); });
    drawer.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-zephyr-wishlist-remove]');
      if (rm) {
        var id = rm.getAttribute('data-zephyr-wishlist-remove');
        lsWrite(LS_WISHLIST, wishlistList().filter(function (it) { return String(it.id) !== String(id); }));
        updateWishlistUI();
        return;
      }
      var add = e.target.closest('[data-zephyr-wishlist-add]');
      if (add) {
        var vid = add.getAttribute('data-zephyr-wishlist-add');
        var orig = add.textContent;
        add.disabled = true; add.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
        }).then(function (r) { return r.json(); }).then(function () {
          add.textContent = 'Added ✓';
          return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) { zwUpdateCartCount(cart.item_count); });
        }).catch(function () { add.textContent = orig; }).then(function () {
          setTimeout(function () { add.textContent = orig; add.disabled = false; }, 1600);
        });
      }
    });
  }

  // ------- RECENTLY VIEWED (track) -------
  function recentlyViewedInit() {
    const isPDP = document.body.classList.contains('template-product') ||
                  !!document.querySelector('main .product');
    if (!isPDP) return;

    // Prefer the wishlist's data-product-handle (set on PDP only) but
    // fall back to the URL pathname.
    const wishlistEl = document.querySelector('[data-zephyr-wishlist]');
    let handle = wishlistEl && wishlistEl.dataset.productHandle;
    if (!handle) {
      const m = location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m && m[1];
    }
    if (!handle) return;

    let recent = lsRead(LS_RECENT);
    recent = recent.filter(h => h !== handle);
    recent.unshift(handle);
    if (recent.length > RECENT_MAX) recent.length = RECENT_MAX;
    lsWrite(LS_RECENT, recent);
  }

  // ------- RECENTLY VIEWED (render) -------
  async function recentRenderInit() {
    const list = document.querySelector('[data-zephyr-recent-list]');
    if (!list) return;

    const empty = document.querySelector('[data-zephyr-recent-empty]');
    let handles = lsRead(LS_RECENT);

    // Drop the current product from "Recently Viewed" so we don't show
    // a duplicate of the page the user is already on.
    const m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (m) handles = handles.filter(h => h !== m[1]);

    if (!handles.length) {
      if (empty) empty.hidden = false;
      return;
    }

    const fragment = document.createDocumentFragment();

    await Promise.all(handles.slice(0, 8).map(async (handle) => {
      try {
        const html = await fetch(`/products/${handle}?section_id=zephyr-product-card`)
          .then(r => r.ok ? r.text() : '');
        if (!html) return;

        // Shopify wraps section response in a top-level element with id
        // "shopify-section-zephyr-product-card". Extract its inner card.
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const card = tmp.querySelector('.card-wrapper, .card, [class*="card"]') || tmp.firstElementChild;
        if (!card) return;

        const li = document.createElement('li');
        li.className = 'grid__item slider__slide';
        li.appendChild(card);
        fragment.appendChild(li);
      } catch (e) {
        console.warn('[zephyr] recently-viewed fetch failed for', handle, e);
      }
    }));

    if (fragment.children.length) {
      list.appendChild(fragment);
      // Keep the related-tabs carousel in sync now that the slides exist:
      // update the slider counter total and let the section re-init its
      // progress bar / arrows.
      const sc = list.closest('slider-component');
      const totalEl = sc && sc.querySelector('.slider-counter--total');
      if (totalEl) totalEl.textContent = list.querySelectorAll('li').length;
      document.dispatchEvent(new CustomEvent('zephyr:recent:rendered'));
      window.dispatchEvent(new Event('resize'));
    } else if (empty) {
      empty.hidden = false;
    }
  }

  // ------- TABS -------
  function tabsInit() {
    const groups = document.querySelectorAll('[data-zephyr-tabs]');
    groups.forEach(group => {
      const tabs = group.querySelectorAll('[data-zephyr-tab]');
      const panels = group.querySelectorAll('[data-zephyr-panel]');
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.zephyrTab;
          tabs.forEach(t => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
          panels.forEach(p => p.toggleAttribute('hidden', p.dataset.zephyrPanel !== target));
        });
      });
    });
  }

  // ------- GRID VIEW DENSITY -------
  function gridViewToggleInit() {
    // Tag Dawn's stock product grid so our CSS can target it
    const dawnGrid = document.getElementById('product-grid');
    if (dawnGrid && !dawnGrid.hasAttribute('data-zephyr-grid')) {
      dawnGrid.setAttribute('data-zephyr-grid', '');
    }

    const toggles = document.querySelectorAll('[data-zephyr-grid-toggle]');
    if (!toggles.length) return;

    const apply = (density) => {
      document.querySelectorAll('[data-zephyr-grid]').forEach(grid => {
        grid.dataset.zephyrGridDensity = density;
      });
      toggles.forEach(t => {
        t.setAttribute('aria-pressed', t.dataset.zephyrGridToggle === density ? 'true' : 'false');
      });
    };

    const stored = localStorage.getItem(LS_GRID_DENSITY) || 'four';
    apply(stored);

    toggles.forEach(t => {
      t.addEventListener('click', () => {
        const next = t.dataset.zephyrGridToggle;
        try { localStorage.setItem(LS_GRID_DENSITY, next); } catch (_) {}
        apply(next);
      });
    });
  }

  // ------- VIEW MORE AJAX -------
  function viewMoreInit() {
    // Only on collection pages (where Dawn's #product-grid exists).
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    // Find Dawn's pagination wrapper and the "next" link inside it.
    // NOTE: Dawn's pagination.liquid SWAPS the arrow classes — the NEXT link gets
    // class `pagination__item--prev` (and there is no rel="next"). So the next page
    // link is `a.pagination__item--prev`.
    const paginationWrap = document.querySelector('.pagination-wrapper, nav.pagination');
    const nextLink = paginationWrap && paginationWrap.querySelector('a[rel="next"], a.pagination__item--prev');

    // No pagination at all → nothing to do.
    if (!paginationWrap) return;

    // Build a button regardless — it might just be "all loaded" state.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'zephyr-view-more';
    btn.textContent = 'View More';
    btn.dataset.idleLabel = 'View More';
    btn.dataset.loadingLabel = 'Loading…';
    if (nextLink) {
      btn.dataset.nextUrl = nextLink.getAttribute('href');
    } else {
      btn.hidden = true;
    }

    // Hide Dawn's numbered pagination, replace with our button.
    paginationWrap.style.display = 'none';
    paginationWrap.parentNode.insertBefore(btn, paginationWrap);

    btn.addEventListener('click', async () => {
      const next = btn.dataset.nextUrl;
      if (!next) { btn.hidden = true; return; }

      btn.disabled = true;
      btn.textContent = btn.dataset.loadingLabel;

      try {
        const html = await fetch(next, { headers: { 'Accept': 'text/html' } }).then(r => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Append product cards from the next page
        const incoming = doc.querySelectorAll('#product-grid > li');
        incoming.forEach(li => grid.appendChild(li));

        // Find the next-next link in the fetched page (Dawn's next link = `--prev`).
        const nextNext = doc.querySelector('.pagination-wrapper a[rel="next"], .pagination-wrapper a.pagination__item--prev, nav.pagination a.pagination__item--prev');
        if (nextNext) {
          btn.dataset.nextUrl = nextNext.getAttribute('href');
          btn.disabled = false;
          btn.textContent = btn.dataset.idleLabel;
        } else {
          btn.hidden = true;
        }

        try { history.pushState(null, '', next); } catch (_) {}
      } catch (e) {
        btn.disabled = false;
        btn.textContent = btn.dataset.idleLabel;
        console.error('[zephyr] view-more failed', e);
      }
    });
  }

  // ------- COLLECTION GRID SYNC -------
  // Dawn's facets.js replaces #ProductGridContainer's innerHTML on every filter/
  // sort change (and our "View More" appends cards), so freshly-rendered cards
  // have no heart listeners. Re-run the guarded wishlistInit() whenever the grid
  // mutates, and keep the custom "Ready to ship" toggle in sync with the URL
  // (it isn't a Dawn .js-filter, so Dawn won't re-render its checked state).
  function collectionGridSyncInit() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    function syncReadyToShip() {
      var toggle = document.querySelector('[data-zg-ready-to-ship]');
      if (!toggle) return;
      try {
        var on = new URLSearchParams(window.location.search).getAll('filter.v.availability').indexOf('1') > -1;
        if (toggle.checked !== on) toggle.checked = on;
      } catch (_) {}
    }

    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        wishlistInit();      // guarded by _zwBound — binds new hearts + refreshes saved state
        syncReadyToShip();
      });
    });
    observer.observe(container, { childList: true, subtree: true });

    syncReadyToShip();
  }

  // ------- CUSTOM SORT DROPDOWN -------
  // The collection "Sort by" is a custom dropdown styled like Category. Each option
  // is a button; clicking it writes the value into the hidden sort_by input and
  // fires the 'input' event Dawn's facets.js debounces → re-sorts with no reload.
  // Delegated on document so it survives facets.js replacing the .sorting markup.
  function zgSortDropdownInit() {
    document.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-zg-sort]');
      if (!opt) return;
      var form = opt.closest('form');
      var input = form ? form.querySelector('[name="sort_by"]') : document.getElementById('SortBy');
      if (input) {
        input.value = opt.getAttribute('data-zg-sort');
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      opt.closest('.zg-cat-list').querySelectorAll('[data-zg-sort]').forEach(function (b) {
        var on = b === opt;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var det = opt.closest('details');
      if (det) det.open = false;
    });
  }

  // ------- BOOT -------
  function boot() {
    wishlistInit();
    wishlistDrawerInit();
    recentlyViewedInit();
    recentRenderInit();
    tabsInit();
    gridViewToggleInit();
    viewMoreInit();
    collectionGridSyncInit();
    zgSortDropdownInit();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
